import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { authService, tareasService } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';
import CodeEditor from '../components/CodeEditor';
import HtmlContent, { stripHtml } from '../components/HtmlContent';
import UploadProgress from '../components/UploadProgress';
import {
  ENTREGA_MAX_ARCHIVOS,
  ENTREGA_MAX_ARCHIVO_BYTES,
  ENTREGA_MAX_ARCHIVO_MB,
  ENTREGA_MAX_SUBIDA_TOTAL_BYTES,
  ENTREGA_MAX_SUBIDA_TOTAL_MB,
  ENTREGA_TEXTO_MAX_CHARS,
  ENTREGA_CODIGO_MAX_CHARS,
  ENTREGA_ENLACE_MAX_CHARS,
} from '../constants/uploadLimits';
import {
  archivoPermitido,
  etiquetaTiposArchivo,
  extensionesDesdeCategorias,
  categoriasArchivoParaTarea,
} from '../constants/tareaArchivoTipos';
import ConfirmPopup from '../components/ConfirmPopup';
import './Tareas.css';

const validarListaArchivos = (files, tarea) => {
  const categorias = categoriasArchivoParaTarea(tarea);
  const permitidas = extensionesDesdeCategorias(categorias, tarea?.extensiones_script);
  const invalido = files.find((f) => !archivoPermitido(f, categorias, tarea?.extensiones_script));
  if (invalido) {
    const lista = [...permitidas].sort().map((e) => `.${e}`).join(', ');
    return `Archivo no permitido: "${invalido.name}". Extensiones admitidas: ${lista}.`;
  }
  return null;
};

const TIPOS_LABELS = { archivo: '📎 Archivo', enlace: '🔗 Enlace', texto: '📝 Texto', codigo: '💻 Código' };

const LENGUAJES_CODIGO = [
  { value: 'plaintext', label: 'Texto plano' },
  { value: 'python', label: 'Python' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'json', label: 'JSON' },
  { value: 'sql', label: 'SQL' },
  { value: 'java', label: 'Java' },
  { value: 'c', label: 'C' },
  { value: 'cpp', label: 'C++' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'go', label: 'Go' },
];

const LENGUAJES_CODIGO_VALUES = new Set(LENGUAJES_CODIGO.map((l) => l.value));

/** Solo fecha YYYY-MM-DD en local (evita desfase UTC). */
const parseDateOnlyLocal = (dateString) => {
  if (!dateString) return null;
  const s = String(dateString).trim().slice(0, 10);
  const parts = s.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
};

/** True si ya pasó la fecha de cierre (o nueva_fecha_cierre / habilitación individual). */
const pasoFechaCierre = (tarea, entrega) => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const base = parseDateOnlyLocal(entrega?.nueva_fecha_cierre || tarea?.fecha_cierre);
  const hab = parseDateOnlyLocal(tarea?.mi_habilitacion_fecha_limite);
  let fechaLimite = base;
  if (hab && (!fechaLimite || hab > fechaLimite)) fechaLimite = hab;
  if (!fechaLimite) return false;
  fechaLimite.setHours(0, 0, 0, 0);
  return hoy > fechaLimite;
};

const EntregaForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tareaId } = useParams();
  const fromCalificaciones = location.state?.from === 'calificaciones';
  const backTo = fromCalificaciones ? '/dashboard/calificaciones' : '/dashboard/mis-cursos';
  const fileInputRef = useRef(null);
  const lenguajeSelectRef = useRef(null);
  const [tarea, setTarea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [lenguajeSelectError, setLenguajeSelectError] = useState(false);
  const [contenido, setContenido] = useState({ texto: '', codigo: '', enlace: '' });
  const [codigoLenguaje, setCodigoLenguaje] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [archivosExistentes, setArchivosExistentes] = useState([]);
  const [archivosEliminar, setArchivosEliminar] = useState([]);
  const [miEntrega, setMiEntrega] = useState(null);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const t = await tareasService.getTarea(tareaId);
        setTarea(t);
        try {
          const entrega = await tareasService.getMiEntrega(tareaId);
          setMiEntrega(entrega || null);
          if (entrega && entrega.comentario) {
            try {
              const data = typeof entrega.comentario === 'string' ? JSON.parse(entrega.comentario) : entrega.comentario;
              setContenido({
                texto: data.texto || '',
                codigo: data.codigo || '',
                enlace: data.enlace || '',
              });
              if (data.codigoLenguaje) setCodigoLenguaje(data.codigoLenguaje);
            } catch (_) {}
          }
          if (entrega?.archivos_list?.length) {
            setArchivosExistentes(entrega.archivos_list);
          } else {
            setArchivosExistentes([]);
          }
          setArchivosEliminar([]);
          setArchivos([]);
        } catch (_) {
          setMiEntrega(null);
          setArchivosExistentes([]);
          setArchivosEliminar([]);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Error al cargar la tarea');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tareaId, navigate]);

  const mostrarError = useCallback((mensaje, { marcarLenguaje = false } = {}) => {
    setLenguajeSelectError(marcarLenguaje);
    setError(mensaje);
    if (marcarLenguaje) {
      requestAnimationFrame(() => {
        lenguajeSelectRef.current?.focus({ preventScroll: true });
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tarea || !tarea.tipos_entrega || tarea.tipos_entrega.length === 0) return;
    setError('');
    setLenguajeSelectError(false);
    const tipos = tarea.tipos_entrega || [];
    const archivosVivos = archivosExistentes.filter((a) => !archivosEliminar.includes(a.id));
    const tieneTexto = tipos.includes('texto') && contenido.texto.trim();
    const tieneCodigo = tipos.includes('codigo') && contenido.codigo.trim();
    const tieneEnlace = tipos.includes('enlace') && contenido.enlace.trim();
    const tieneArchivo = tipos.includes('archivo') && (archivos.length > 0 || archivosVivos.length > 0);
    if (!tieneTexto && !tieneCodigo && !tieneEnlace && !tieneArchivo) {
      mostrarError('Debe completar al menos uno de los campos según los tipos de entrega permitidos.');
      return;
    }
    if (tipos.includes('texto') && contenido.texto.length > ENTREGA_TEXTO_MAX_CHARS) {
      mostrarError(`El texto supera el límite de ${ENTREGA_TEXTO_MAX_CHARS.toLocaleString('es-AR')} caracteres.`);
      return;
    }
    if (tieneCodigo && (!codigoLenguaje || !LENGUAJES_CODIGO_VALUES.has(codigoLenguaje))) {
      mostrarError('Si entrega código, debe seleccionar el lenguaje en el desplegable.', { marcarLenguaje: true });
      return;
    }
    if (tipos.includes('codigo') && contenido.codigo.length > ENTREGA_CODIGO_MAX_CHARS) {
      mostrarError(`El código supera el límite de ${ENTREGA_CODIGO_MAX_CHARS.toLocaleString('es-AR')} caracteres.`);
      return;
    }
    if (tipos.includes('enlace') && contenido.enlace.length > ENTREGA_ENLACE_MAX_CHARS) {
      mostrarError(`El enlace supera el límite de ${ENTREGA_ENLACE_MAX_CHARS.toLocaleString('es-AR')} caracteres.`);
      return;
    }
    if (archivos.length > 0) {
      const msgArch = validarListaArchivos(archivos, tarea);
      if (msgArch) {
        mostrarError(msgArch);
        return;
      }
    }
    if (archivosVivos.length + archivos.length > ENTREGA_MAX_ARCHIVOS) {
      mostrarError(`Como máximo ${ENTREGA_MAX_ARCHIVOS} archivos por entrega.`);
      return;
    }
    let totalBytes = 0;
    for (const file of archivos) {
      if (file.size > ENTREGA_MAX_ARCHIVO_BYTES) {
        mostrarError(`Cada archivo puede pesar como máximo ${ENTREGA_MAX_ARCHIVO_MB} MB: "${file.name}".`);
        return;
      }
      totalBytes += file.size;
    }
    if (totalBytes > ENTREGA_MAX_SUBIDA_TOTAL_BYTES) {
      mostrarError(
        `El peso total de los archivos supera ${ENTREGA_MAX_SUBIDA_TOTAL_MB} MB (límite de subida). Quite archivos o comprima antes de enviar.`
      );
      return;
    }
    setSubmitting(true);
    setUploadProgress(0);
    try {
      const payload = {
        texto: contenido.texto.trim() || undefined,
        codigo: contenido.codigo.trim() || undefined,
        enlace: contenido.enlace.trim() || undefined,
        codigoLenguaje: tieneCodigo ? codigoLenguaje : undefined,
      };
      const formData = new FormData();
      formData.append('comentario', JSON.stringify(payload));
      archivos.forEach((file) => formData.append('archivos', file));
      if (archivosEliminar.length > 0) {
        formData.append('archivos_eliminar', JSON.stringify(archivosEliminar));
      }

      await tareasService.submitEntrega(tareaId, formData, {
        onUploadProgress: (percent) => setUploadProgress(percent),
      });
      setUploadProgress(100);
      navigate(backTo, fromCalificaciones ? undefined : { state: { fromEntrega: true } });
    } catch (err) {
      mostrarError(err.response?.data?.error || 'Error al enviar la entrega');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const tipos = (tarea && tarea.tipos_entrega) || [];
  const hasArchivo = tipos.includes('archivo');
  const hasTexto = tipos.includes('texto');
  const hasCodigo = tipos.includes('codigo');
  const hasEnlace = tipos.includes('enlace');

  const esReentrega = Boolean(miEntrega?.pedir_reentrega);
  const esEdicion = Boolean(miEntrega && !miEntrega.pedir_reentrega && !miEntrega.revisado);
  const plazoOk = !pasoFechaCierre(tarea, miEntrega) || Boolean(tarea?.aceptar_vencidos);
  const entregaBloqueada = Boolean(
    (miEntrega && miEntrega.revisado && !miEntrega.pedir_reentrega)
    || (miEntrega && !miEntrega.pedir_reentrega && !miEntrega.revisado && !plazoOk)
    || (miEntrega && miEntrega.pedir_reentrega && !plazoOk)
    || (!miEntrega && !plazoOk)
  );
  const puedeEnviar = !entregaBloqueada;
  const entregaEnGrupos = Boolean(tarea?.entrega_en_grupos);
  const archivosVivosCount = archivosExistentes.filter((a) => !archivosEliminar.includes(a.id)).length;

  if (loading) {
    return (
      <div className="tareas-container">
        <div className="tareas-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
      </div>
    );
  }

  if (!tarea) {
    return (
      <div className="tareas-container">
        <p className="tareas-form-error">{error || 'Tarea no encontrada'}</p>
        <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => navigate(backTo)}>{fromCalificaciones ? 'Volver a Calificaciones' : 'Volver a Mis Cursos'}</button>
      </div>
    );
  }

  return (
    <div className="tareas-container">
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <div className="tareas-form-page-header">
        <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => navigate(backTo)}>
          ← Volver
        </button>
        <h1>{esEdicion ? 'Modificar entrega:' : esReentrega ? 'Reentregar:' : 'Entregar:'} {tarea.titulo || 'Tarea'}</h1>
      </div>
      {tarea.descripcion && stripHtml(tarea.descripcion) && (
        <div className="entrega-consigna-block">
          <h2 className="entrega-consigna-titulo">Descripción de la tarea</h2>
          <HtmlContent content={tarea.descripcion} tag="div" className="entrega-consigna-texto html-content" />
        </div>
      )}
      {tarea.archivos_material?.length > 0 && (
        <div className="entrega-material-block">
          <h2 className="entrega-consigna-titulo">Material de la actividad</h2>
          <p className="tareas-form-hint">Archivos proporcionados por el docente para realizar la tarea.</p>
          <div className="tareas-correccion-archivos-grid">
            {tarea.archivos_material.map((a) => (
              <div key={a.id} className="tareas-correccion-archivo-card">
                <span className="tareas-correccion-archivo-icono" aria-hidden>📎</span>
                <span className="tareas-correccion-archivo-nombre" title={a.nombre_original || 'Archivo'}>
                  {a.nombre_original || 'Archivo'}
                </span>
                <button
                  type="button"
                  className="tareas-correccion-archivo-descargar"
                  onClick={() => tareasService.descargarArchivoTarea(a.id, a.nombre_original)}
                >
                  Descargar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {miEntrega && (miEntrega.devolucion || miEntrega.pedir_reentrega) && (
        <div className="entrega-devolucion-block">
          {miEntrega.pedir_reentrega && miEntrega.nueva_fecha_cierre && (
            <div className="entrega-reentrega-aviso">
              Debe reentregar esta tarea antes del{' '}
              <strong>{new Date(miEntrega.nueva_fecha_cierre).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
            </div>
          )}
          {miEntrega.devolucion && (
            <div className="entrega-devolucion-contenido">
              <strong>Devolución del profesor:</strong>
              <HtmlContent content={miEntrega.devolucion} tag="div" className="entrega-devolucion-texto html-content" />
            </div>
          )}
        </div>
      )}
      {entregaBloqueada && (
        <div className="entrega-devolucion-block entrega-ya-corregida-aviso">
          <p>
            {miEntrega?.revisado && !miEntrega?.pedir_reentrega
              ? (entregaEnGrupos
                ? 'Su grupo ya tiene una entrega corregida. Solo pueden volver a enviarla si el docente pidió una reentrega.'
                : 'Su entrega ya fue corregida. Solo puede volver a enviarla si el docente le pidió una reentrega.')
              : (entregaEnGrupos
                ? 'El plazo de entrega venció. Ya no pueden modificar la entrega.'
                : 'El plazo de entrega venció. Ya no puede modificar la entrega.')}
          </p>
          <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => navigate(backTo)}>
            Volver
          </button>
        </div>
      )}
      {puedeEnviar && esEdicion && (
        <p className="tareas-form-hint" style={{ marginBottom: '1rem' }}>
          Ya entregaste esta tarea. Podés modificar el contenido mientras no haya vencido ni haya sido corregida.
        </p>
      )}
      {puedeEnviar && (
      <form onSubmit={handleSubmit} className="tareas-form tareas-form-page">
        {hasTexto && (
          <div className="tareas-form-group">
            <label>Texto</label>
            <div className="entrega-texto-wrap">
              <RichTextEditor
                value={contenido.texto}
                onChange={(html) => setContenido((p) => ({ ...p, texto: html }))}
                placeholder="Escriba su entrega en texto..."
                minHeight="200px"
                allowImageUpload
              />
            </div>
          </div>
        )}

        {hasCodigo && (
          <div className="tareas-form-group">
            <label>Código</label>
            <div className="entrega-codigo-row">
              <label htmlFor="entrega-codigo-lenguaje" className="entrega-codigo-lenguaje-label">
                Lenguaje {contenido.codigo.trim() ? '*' : ''}
              </label>
              <select
                ref={lenguajeSelectRef}
                id="entrega-codigo-lenguaje"
                value={codigoLenguaje}
                onChange={(e) => {
                  setCodigoLenguaje(e.target.value);
                  setLenguajeSelectError(false);
                }}
                className={`tareas-form-select${lenguajeSelectError ? ' entrega-codigo-select--error' : ''}`}
                aria-invalid={lenguajeSelectError}
              >
                <option value="">Seleccione un lenguaje</option>
                {LENGUAJES_CODIGO.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="entrega-codigo-wrap">
              <CodeEditor
                value={contenido.codigo}
                onChange={(code) => setContenido((p) => ({ ...p, codigo: code }))}
                language={codigoLenguaje || 'plaintext'}
                placeholder="Pegue o escriba su código..."
                className="entrega-codigo-editor"
              />
            </div>
          </div>
        )}

        {hasEnlace && (
          <div className="tareas-form-group">
            <label>Enlace</label>
            <input
              type="url"
              value={contenido.enlace}
              onChange={(e) => setContenido((p) => ({ ...p, enlace: e.target.value }))}
              placeholder="https://..."
            />
          </div>
        )}

        {hasArchivo && (
          <div className="tareas-form-group">
            <label>Archivos</label>
            <p className="entrega-archivo-limite">
              Puede elegir varios archivos. La suma de todos no puede superar {ENTREGA_MAX_SUBIDA_TOTAL_MB} MB.
            </p>
            {tarea && (
              <p className="entrega-archivo-extensiones">
                <strong>Extensiones permitidas:</strong>{' '}
                {etiquetaTiposArchivo(categoriasArchivoParaTarea(tarea), tarea?.extensiones_script) || '—'}
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="tareas-form-file-input-hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                const msgArch = validarListaArchivos(picked, tarea);
                if (msgArch) {
                  mostrarError(msgArch);
                  e.target.value = '';
                  return;
                }
                const espacio = ENTREGA_MAX_ARCHIVOS - archivosVivosCount - archivos.length;
                if (espacio <= 0) {
                  mostrarError(`Como máximo ${ENTREGA_MAX_ARCHIVOS} archivos por entrega.`);
                  e.target.value = '';
                  return;
                }
                setArchivos((prev) => [...prev, ...picked.slice(0, espacio)]);
                if (picked.length > espacio) {
                  mostrarError(`Solo se agregaron ${espacio} archivo(s). El límite es ${ENTREGA_MAX_ARCHIVOS}.`);
                }
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="tareas-btn tareas-btn-secondary tareas-btn-add-file"
              onClick={() => fileInputRef.current?.click()}
              disabled={archivosVivosCount + archivos.length >= ENTREGA_MAX_ARCHIVOS}
            >
              📎 Agregar archivo(s)
            </button>
            {archivosExistentes.filter((a) => !archivosEliminar.includes(a.id)).length > 0 && (
              <ul className="entrega-archivos-list">
                {archivosExistentes.filter((a) => !archivosEliminar.includes(a.id)).map((a) => (
                  <li key={`ex-${a.id}`}>
                    <span>{a.nombre_original || 'Archivo'}</span>
                    <button
                      type="button"
                      className="tareas-btn-remove-file"
                      onClick={() => setArchivosEliminar((prev) => (prev.includes(a.id) ? prev : [...prev, a.id]))}
                      title="Quitar"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {archivos.length > 0 && (
              <ul className="entrega-archivos-list">
                {archivos.map((f, i) => (
                  <li key={`${f.name}-${f.size}-${i}`}>
                    <span>{f.name}</span>
                    <button
                      type="button"
                      className="tareas-btn-remove-file"
                      onClick={() => setArchivos((prev) => prev.filter((_, idx) => idx !== i))}
                      title="Quitar"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {submitting && (
          <div className="tareas-form-upload-progress">
            <UploadProgress
              percent={uploadProgress}
              label={uploadProgress >= 100 ? 'Listo' : `Subiendo entrega… ${uploadProgress}%`}
            />
          </div>
        )}
        <div className="tareas-modal-actions">
          <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => navigate(backTo)} disabled={submitting}>Cancelar</button>
          <button type="submit" className="tareas-btn tareas-btn-primary" disabled={submitting}>
            {submitting
              ? (uploadProgress >= 100 ? 'Enviando...' : 'Subiendo...')
              : esReentrega
                ? 'Enviar reentrega'
                : esEdicion
                  ? 'Guardar cambios'
                  : 'Enviar entrega'}
          </button>
        </div>
      </form>
      )}
    </div>
  );
};

export default EntregaForm;
